<template>
  <b-modal
    :visible="visible"
    :title="booking ? `Editar reserva ${booking.folio}` : 'Editar reserva'"
    size="lg"
    no-close-on-backdrop
    :no-close-on-esc="isSaving"
    :hide-header-close="isSaving"
    hide-footer
    @show="reset"
    @hidden="$emit('close')"
  >
    <validation-observer v-if="booking" ref="form" #default="{ invalid }">
      <b-row>
        <b-col md="12" class="mb-1">
          <b>Estatus:</b> {{ booking.statusname }}
        </b-col>
        <b-col md="4">
          <validation-provider name="Llegada" rules="required" #default="{ errors }">
            <b-form-group label="Llegada">
              <b-form-input v-model="form.dateIn" type="date" :state="errors[0] ? false : null" />
              <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>
        </b-col>
        <b-col md="4">
          <validation-provider name="Salida" rules="required" #default="{ errors }">
            <b-form-group label="Salida">
              <b-form-input v-model="form.dateOut" type="date" :state="errors[0] || dateOrderInvalid ? false : null" />
              <b-form-invalid-feedback>{{ errors[0] || 'La salida no puede ser anterior a la llegada' }}</b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>
        </b-col>
        <b-col md="4">
          <validation-provider name="Motivo" rules="required" #default="{ errors }">
            <b-form-group label="Motivo">
              <b-form-select v-model="form.reason" :options="reasonOptions" :state="errors[0] ? false : null" />
              <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>
        </b-col>
        <b-col md="4">
          <validation-provider name="Adultos" :rules="adultsRules" #default="{ errors }">
            <b-form-group label="Adultos">
              <b-form-input v-model="form.adults" type="number" min="1" :max="maxAdults" :state="errors[0] ? false : null" />
              <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>
        </b-col>
        <b-col md="4">
          <validation-provider name="Menores" :rules="childrenRules" #default="{ errors }">
            <b-form-group label="Menores">
              <b-form-input v-model="form.children" type="number" min="0" :max="maxChildren" :state="errors[0] ? false : null" />
              <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>
        </b-col>
        <b-col v-if="isConfirmed" md="4">
          <validation-provider name="Localizador" rules="required" #default="{ errors }">
            <b-form-group label="Localizador Avantio">
              <b-form-input v-model="form.reservanumber" type="text" maxlength="255" :state="errors[0] ? false : null" />
              <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>
        </b-col>
      </b-row>

      <h6 class="mt-1">Titular</h6>
      <b-row>
        <b-col md="6">
          <validation-provider name="Nombre" rules="required" #default="{ errors }">
            <b-form-group label="Nombre">
              <b-form-input v-model="form.holder.name" type="text" maxlength="255" :state="errors[0] ? false : null" />
              <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>
        </b-col>
        <b-col md="6">
          <validation-provider name="Apellido" rules="required" #default="{ errors }">
            <b-form-group label="Apellido">
              <b-form-input v-model="form.holder.lastname" type="text" maxlength="255" :state="errors[0] ? false : null" />
              <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>
        </b-col>
      </b-row>

      <b-form-group label="Solicitud especial">
        <b-form-textarea v-model="form.notes" rows="3" maxlength="255" />
      </b-form-group>

      <div class="text-right">
        <b-button variant="secondary" class="mr-1" :disabled="isSaving" @click="$emit('close')">Cerrar</b-button>
        <b-button variant="primary" :disabled="invalid || dateOrderInvalid || isSaving" @click="save">
          <b-spinner small v-if="isSaving" /> Guardar
        </b-button>
      </div>
    </validation-observer>
  </b-modal>
</template>

<script>
import { xcalacocoBookingReasonOptions } from '@/data/xcalacocoBookings'

function withMax(rules, max) {
  return max === null ? rules : `${rules}|max_value:${max}`
}

function emptyForm() {
  return {
    dateIn: '',
    dateOut: '',
    reason: null,
    adults: 1,
    children: 0,
    holder: { name: '', lastname: '' },
    notes: '',
    reservanumber: '',
  }
}

export default {
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    booking: {
      type: Object,
      default: null,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
    maxAdults: {
      type: Number,
      default: null,
    },
    maxChildren: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      form: emptyForm(),
      reasonOptions: xcalacocoBookingReasonOptions,
    }
  },
  computed: {
    isConfirmed() {
      return Boolean(this.booking) && String(this.booking.status) === '2'
    },
    adultsRules() {
      return withMax('required|integer|min_value:1', this.maxAdults)
    },
    childrenRules() {
      return withMax('required|integer|min_value:0', this.maxChildren)
    },
    dateOrderInvalid() {
      return Boolean(this.form.dateIn && this.form.dateOut && this.form.dateOut < this.form.dateIn)
    },
  },
  methods: {
    reset() {
      const { booking } = this
      if (!booking) {
        this.form = emptyForm()
        return
      }
      // Only the holder is sent now; legacy records may hold several guests, the first is the holder
      const holder = (Array.isArray(booking.guests) && booking.guests[0]) || {}
      this.form = {
        dateIn: booking.dateIn || '',
        dateOut: booking.dateOut || '',
        reason: booking.reason === null || booking.reason === undefined ? null : String(booking.reason),
        adults: booking.adults,
        children: booking.children,
        holder: { name: holder.name || '', lastname: holder.lastname || '' },
        notes: booking.notes || '',
        reservanumber: booking.reservanumber || '',
      }
    },
    async save() {
      if (this.isSaving || this.dateOrderInvalid) return
      const isValid = await this.$refs.form.validate()
      if (!isValid) return
      const { form } = this
      const body = {
        id: this.booking.id,
        dateIn: form.dateIn,
        dateOut: form.dateOut,
        reason: form.reason,
        adults: parseInt(form.adults, 10),
        children: parseInt(form.children, 10),
        guests: [{ name: form.holder.name.trim(), lastname: form.holder.lastname.trim() }],
        notes: form.notes.trim(),
      }
      if (this.isConfirmed) body.reservanumber = form.reservanumber.trim()
      this.$emit('save', body)
    },
  },
}
</script>
